import type { SWRConfiguration } from 'swr';
import { isAbortError, isNetworkConnectivityError } from '@parcel2go/npm-p2g-app';
import { capturePromiseException, captureWarning } from '~/lib/metrics';
import { ApiResponse } from '~/lib/api/api-response';

// Derived structurally from the public SWRConfiguration type rather than importing
// PublicConfiguration/Revalidator from the unstable `swr/_internal` deep import.
type OnErrorRetry = NonNullable<SWRConfiguration['onErrorRetry']>;
type OnErrorRetryConfig = Parameters<OnErrorRetry>[2];
type OnErrorRetryRevalidate = Parameters<OnErrorRetry>[3];

/** How often the notification counts refresh once a fetch has succeeded. */
export const BACKGROUND_TASK_REFRESH_MS = 15 * 60000;

/**
 * Thrown by fetchBackgroundTaskJson for responses that indicate a real failure (as opposed to
 * the 401/403/404 "nothing to show" cases it treats as empty results).
 */
export class BackgroundTaskFetchError extends Error {
  constructor(
    public readonly taskName: string,
    public readonly status: number,
    public readonly contentType: string,
    public readonly cause?: unknown
  ) {
    super(`Background task "${taskName}" received ${status} (content-type: ${contentType || 'none'})`);
    this.name = 'BackgroundTaskFetchError';
  }
}

/**
 * Thrown by requireBackgroundTaskSuccess for a 200 `success:false` envelope - the edge's way of
 * saying an upstream call failed. The message is constant per task because it is the Sentry
 * grouping key; the edge's own message is kept on `reason`.
 */
export class BackgroundTaskRejectedError extends Error {
  constructor(
    public readonly taskName: string,
    public readonly reason?: string
  ) {
    super(`Background task "${taskName}" was rejected`);
    this.name = 'BackgroundTaskRejectedError';
  }
}

/**
 * Refusals the my-account edge gives the same answer to on every attempt: "Not Allowed" (the token
 * is not linked to an account) and "Invalid Request". Retrying them only adds load, so they keep
 * meaning "nothing to show".
 */
const FIXED_REFUSALS = ['Not Allowed', 'Invalid Request'];

/**
 * Treats a 200 `success:false` envelope as the failed request it is, so the poll retries with the
 * same backoff as a 5xx. The my-account edge returns its `CreateFail` envelope, rather than a 500,
 * when the Accounts API call behind surcharges or refunds fails.
 */
export function requireBackgroundTaskSuccess<T>(res: ApiResponse<T> | null, taskName: string): ApiResponse<T> | null {
  if (!res || res.success) {
    return res;
  }

  const reason = res.error?.message;
  if (reason && FIXED_REFUSALS.includes(reason)) {
    return null;
  }

  throw new BackgroundTaskRejectedError(taskName, reason);
}

/**
 * Awaits a background-task Response and returns parsed JSON, or null for statuses that mean
 * "nothing to show" rather than "something broke" (401/403/404 - unauthenticated, unauthorised,
 * or the feature simply has no data for this tenant). Never calls res.json() on a non-JSON or
 * non-2xx body, which is what previously produced a flood of "Unexpected end of JSON input" /
 * Firefox "The string did not match the expected pattern" errors on every polling failure.
 */
export async function fetchBackgroundTaskJson<T>(
  responsePromise: Promise<Response>,
  taskName: string
): Promise<ApiResponse<T> | null> {
  const res = await responsePromise;

  if (res.status === 401 || res.status === 403 || res.status === 404) {
    return null;
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (!res.ok || !contentType.includes('application/json')) {
    throw new BackgroundTaskFetchError(taskName, res.status, contentType);
  }

  try {
    return (await res.json()) as ApiResponse<T>;
  } catch (e) {
    throw new BackgroundTaskFetchError(taskName, res.status, contentType, e);
  }
}

/**
 * Schedules a revalidate attempt after `BACKGROUND_TASK_REFRESH_MS`, gated the same way SWR's own
 * refreshInterval polling gates itself (`refreshWhenHidden || isVisible()`, `refreshWhenOffline ||
 * isOnline()`, see swr's `execute()`). Calling `revalidate` directly on a bare timer would bypass
 * that check entirely, so a hidden/backgrounded tab would keep firing full retry bursts (and a
 * Sentry report) every cycle regardless of `refreshWhenHidden: false` - exactly the background
 * load this config exists to avoid. If the tab still isn't active when the timer fires, just
 * check again next cycle instead of revalidating.
 */
function scheduleRecoveryRevalidate(revalidate: OnErrorRetryRevalidate, config: OnErrorRetryConfig) {
  setTimeout(() => {
    const canRevalidate =
      (config.refreshWhenHidden || config.isVisible()) && (config.refreshWhenOffline || config.isOnline());
    if (canRevalidate) {
      revalidate({ retryCount: 0, dedupe: true });
    } else {
      scheduleRecoveryRevalidate(revalidate, config);
    }
  }, BACKGROUND_TASK_REFRESH_MS);
}

/**
 * Shared SWR policy for the notification background tasks (surcharges, refunds, claims,
 * support). These poll low-priority badge counts, so a failure should never cost more than the
 * data is worth.
 *
 * It previously used `refreshInterval: (d) => (d === undefined ? 100 : 15 * 60000)`. Because a
 * failed fetch leaves `data` undefined, any error pinned the hook at ten requests a second for
 * as long as the tab stayed focused. That is self-sustaining: the retries add load, the load
 * causes more failures, and the failures keep `data` undefined. Four such tasks run at once.
 *
 * The 100ms tick was also doing double duty as the initial load, since `revalidateOnMount` was
 * false. That job now sits where it belongs, and failures fall to SWR's randomised exponential
 * backoff instead.
 */
export const backgroundTaskSwrConfig: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateIfStale: false,
  // Performs the initial load that the 100ms refreshInterval used to stand in for.
  revalidateOnMount: true,
  refreshWhenHidden: false,
  refreshInterval: BACKGROUND_TASK_REFRESH_MS,
  // SWR scales this by 2^attempt with jitter, giving roughly 5s, 10s, 20s, 40s, 80s before it
  // stops and waits for the next scheduled refresh.
  errorRetryInterval: 5000,
  errorRetryCount: 5,
  // SWR increments `retryCount` itself before calling this (it's already reflected in `opts`
  // below), so retrying via `revalidate(opts)` unchanged - not a re-incremented copy - is what
  // keeps `errorRetryCount` retries actually meaning 5 retries and preserves SWR's own
  // exponential-backoff-with-jitter timing (mirrored below since overriding onErrorRetry means
  // that default no longer runs).
  //
  // SWR's periodic refreshInterval polling also refuses to revalidate at all while an error is
  // cached (see swr's `execute()`), and error is only cleared by a *successful* fetch. So once a
  // retry burst gives up, simply returning would leave this task permanently stuck until the
  // page remounts. Instead, report the burst's one terminal failure, then let
  // scheduleRecoveryRevalidate start a fresh burst so a resolved outage is picked up
  // automatically - without ignoring `refreshWhenHidden` in the meantime.
  onErrorRetry: (error, key, config, revalidate, opts) => {
    const maxRetryCount = config.errorRetryCount ?? 5;
    const { retryCount } = opts;

    // A request the browser cancelled - most often the redirect that follows a failed silent
    // sign-in after an idle tab resumes - is not a failure of this task. Retrying it only races the
    // navigation, so leave it to the next scheduled cycle and report nothing.
    if (isAbortError(error)) {
      scheduleRecoveryRevalidate(revalidate, config);
      return;
    }

    if (retryCount > maxRetryCount) {
      // Connectivity failures ("Failed to fetch", "Load failed", "NetworkError ...") are
      // environmental, and include the in-flight requests a navigation tears down. The package's
      // own fetchers do not report them, and bespoke fetchers are asked to apply the same gate.
      // Tagged so main.tsx's beforeSend can identify this as a background-poll failure without
      // guessing from the request URL/domain, which foreground features also call.
      const tags = { 'p2g-source': 'background-task', 'p2g-background-task-key': key };
      if (error instanceof BackgroundTaskRejectedError) {
        // The edge answered and has already logged the upstream failure, so this is a warning,
        // not an exception.
        captureWarning(error.message, tags);
      } else if (!isNetworkConnectivityError(error)) {
        capturePromiseException(error, tags);
      }
      scheduleRecoveryRevalidate(revalidate, config);
      return;
    }

    const timeout =
      ~~((Math.random() + 0.5) * (1 << (retryCount < 8 ? retryCount : 8))) * (config.errorRetryInterval ?? 5000);
    setTimeout(revalidate, timeout, opts);
  }
};
